<script setup lang="ts">
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import { TransportType } from '@libs/trip-planner-interfaces';

import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';
import type { AvailableItineraryFerrySegment } from '../../../models';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ChangeProductLink from '../ChangeProductLink.vue';

import FerrySegmentHeader from './FerrySegmentHeader.vue';
import FerryProductCard from './FerryProductCard.vue';

interface AvailableFerrySegmentProps {
  segment: AvailableItineraryFerrySegment;
  printMode: boolean;
}

const props = defineProps<AvailableFerrySegmentProps>();

interface AvailableFerrySegmentEmits {
  (eventName: 'change-upsell'): void;
  (eventName: 'remove-filter', tagId: string): void;
  (eventName: 'select-transport', transportType: TransportType): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
}

const emit = defineEmits<AvailableFerrySegmentEmits>();
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    class="available-ferry-segment"
  >
    <template #dropdown>
      <ItineraryTransportSelector
        :transport-type="TransportType.Ferry"
        :disabled="props.printMode"
        @select-transport="emit('select-transport', $event)"
      />
    </template>
    <template #header>
      <FerrySegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
      />
    </template>

    <template #content>
      <div class="available-ferry-segment-content-wrapper">
        <FerryProductCard
          :product-card-data="props.segment.productCardData"
          :print-mode="props.printMode"
          @change-upsell="emit('change-upsell')"
          @show-details="emit('show-details')"
        />
        <ChangeProductLink
          v-if="!props.printMode"
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.available-ferry-segment {
  .available-ferry-segment-content-wrapper {
    display: flex;
    flex-flow: column;
    gap: var(--spacing-16);

    .change-product-link {
      align-self: flex-end;
    }
  }
}
</style>
